import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRemoveOrthodontistUser } from '../../../../hooks/useOrthodontistUsers';
import { useToast } from '../../../../providers/ToastProvider';
import type { OrthodontistUserRow } from '../../../../services/orthodontistAccounts';
import { apiErrorMessage } from '../../../../utils/apiError';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { UserCell } from '../../users/UserCell';

interface RemoveOrthodontistUserModalProps {
    orthodontistId: string;
    orthodontistName: string;
    user: OrthodontistUserRow;
    onClose: () => void;
}

// Unlinks the user from this org only - their account and any practice
// memberships are untouched, hence the reassuring sub-copy.
export function RemoveOrthodontistUserModal({
    orthodontistId,
    orthodontistName,
    user,
    onClose
}: RemoveOrthodontistUserModalProps) {
    const { showToast } = useToast();
    const removeUser = useRemoveOrthodontistUser();

    useEscapeKey(onClose, !removeUser.isPending);

    const handleConfirm = async () => {
        try {
            await removeUser.mutateAsync({ orthodontistId, userId: user.userId });
            showToast(`${user.name} removed from ${orthodontistName}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to remove user'), 'error');
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-orthodontist-user-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[460px] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-red-50 text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="remove-orthodontist-user-modal-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Remove {user.name} from {orthodontistName}?
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            They keep their account and any practice memberships.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={removeUser.isPending}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-5">
                    <div className="border border-gray-200 rounded-lg px-3.5 py-3">
                        <UserCell name={user.name} email={user.email} size={32} />
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={removeUser.isPending}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={removeUser.isPending}
                        onClick={handleConfirm}
                        className="bg-red-600 text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                        {removeUser.isPending ? 'Removing…' : 'Remove'}
                    </button>
                </div>
            </div>
        </div>
    );
}
